/**
 * Adapter Compatibility Guide modal (finder products).
 *
 * Markup: snippets/component-adapter-compatibility-guide.liquid
 * Docs:   .cursor/references/pdp-car-seat-adapter-finder.md
 *
 * Opens from the same triggers the legacy guide used
 * (`.compatibility-guide__link`, `.compatibility-warning span`), switches
 * brand tabs, and exposes `selectBrand(name)` so the finder can pre-select
 * the shopper's brand.
 */

if (!customElements.get('adapter-compatibility-guide')) {
  customElements.define(
    'adapter-compatibility-guide',
    class AdapterCompatibilityGuide extends HTMLElement {
      static TRIGGER_SELECTOR = '.compatibility-guide__link, .compatibility-warning span';

      opener = null;
      onKeydown = null;
      onTriggerClick = null;

      connectedCallback() {
        this.dialog = this.querySelector('[data-guide-dialog]');
        this.tabs = Array.from(this.querySelectorAll('[data-guide-brand]'));
        this.panels = Array.from(this.querySelectorAll('[data-guide-panel]'));

        this.addEventListener('click', (event) => {
          const close = event.target.closest('[data-guide-close]');
          if (close && this.contains(close)) {
            this.close();
            return;
          }
          const tab = event.target.closest('[data-guide-brand]');
          if (tab && this.contains(tab)) this.selectBrand(tab.dataset.guideBrand, true);
        });

        const tablist = this.querySelector('[role="tablist"]');
        if (tablist) tablist.addEventListener('keydown', (e) => this.onTablistKeydown(e));

        // Triggers live outside this element (finder result link, validation
        // notice, red notice span) and INSIDE <product-form>, whose click
        // handler calls stopImmediatePropagation() — document-level
        // delegation never sees them, so bind each trigger directly (same as
        // the legacy guide). The script is deferred, so they all exist.
        this.onTriggerClick = (event) => {
          event.preventDefault();
          this.open(event.currentTarget);
        };
        this.triggers = Array.from(document.querySelectorAll(AdapterCompatibilityGuide.TRIGGER_SELECTOR)).filter(
          (el) => !this.contains(el),
        );
        for (const trigger of this.triggers) trigger.addEventListener('click', this.onTriggerClick);
      }

      disconnectedCallback() {
        for (const trigger of this.triggers || []) trigger.removeEventListener('click', this.onTriggerClick);
        this.close();
      }

      get isOpen() {
        return !this.hidden;
      }

      /** @param {Element} [opener] element to return focus to on close */
      open(opener) {
        if (this.isOpen) return;
        this.opener = opener || document.activeElement;
        this.hidden = false;
        document.body.classList.add('adapter-guide-open');
        this.onKeydown = (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
            return;
          }
          if (event.key === 'Tab') this.trapFocus(event);
        };
        document.addEventListener('keydown', this.onKeydown);
        if (this.dialog) {
          this.dialog.scrollTop = 0;
          this.dialog.focus({ preventScroll: true });
        }
      }

      /**
       * Keep Tab / Shift+Tab cycling inside the dialog while it is open
       * (WCAG 2.1.2). Inactive tabs have tabindex=-1 so only the selected
       * brand tab is in the cycle, as the tabs pattern expects.
       * @param {KeyboardEvent} event
       */
      trapFocus(event) {
        if (!this.dialog) return;
        // tabindex=-1 elements (inactive tabs) are focusable but not tabbable.
        const focusable = Array.from(
          this.dialog.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]',
          ),
        ).filter((el) => el.offsetParent !== null && el.getAttribute('tabindex') !== '-1');
        if (!focusable.length) {
          event.preventDefault();
          this.dialog.focus({ preventScroll: true });
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || active === this.dialog || !this.dialog.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || !this.dialog.contains(active))) {
          event.preventDefault();
          first.focus();
        }
      }

      close() {
        if (!this.isOpen) return;
        this.hidden = true;
        document.body.classList.remove('adapter-guide-open');
        if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
        this.onKeydown = null;
        if (this.opener && typeof this.opener.focus === 'function') this.opener.focus({ preventScroll: true });
        this.opener = null;
      }

      /**
       * Show the panel for a brand. Case-insensitive; unknown brands are ignored.
       * @param {string} name
       * @param {boolean} [focusTab]
       */
      selectBrand(name, focusTab = false) {
        const key = String(name || '')
          .trim()
          .toLowerCase();
        const tab = this.tabs.find((t) => t.dataset.guideBrand.toLowerCase() === key);
        if (!tab) return false;
        for (const t of this.tabs) {
          const active = t === tab;
          t.classList.toggle('adapter-guide__tab--active', active);
          t.setAttribute('aria-selected', String(active));
          t.tabIndex = active ? 0 : -1;
        }
        for (const p of this.panels) p.hidden = p.dataset.guidePanel.toLowerCase() !== key;
        if (focusTab) tab.focus({ preventScroll: true });
        return true;
      }

      onTablistKeydown(event) {
        const current = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
        let next = null;
        if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
        else if (event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.tabs.length - 1;
        if (next === null) return;
        event.preventDefault();
        this.selectBrand(this.tabs[next].dataset.guideBrand, true);
      }
    },
  );
}
